import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { AgGridReact } from 'ag-grid-react';
import { connect } from 'react-redux';
import Fa from 'react-fontawesome';
import { indexOf, reduce, delay } from 'lodash';
import Helmet from 'react-helmet';
import { Link } from 'react-router-dom';
import { Tooltip, OverlayTrigger } from 'react-bootstrap';

import ReactResizeDetector from 'react-resize-detector';
import CustomHeader from '../../../../components/god-table/header';
import ProgressBar from '../../../../components/progress-bar';
import SearchField from '../../../../components/search-field';
import FilterButton from '../../components/FilterButton/FilterButton';

// Import Actions

// Import Selectors
import { getFilters, getLoading, getLoaded, getHasErrors, getErrors } from '../../FilterReducer';
import { getViewableDimensions } from '../../../App/AppReducer';

function LabelCellRenderer(props) {
  const { value, data } = props;
  let linkable = (data.type === 'picklist' || data.type === 'multipicklist') && !data.is_boolean;
  if (data.$fieldMetaType === 'email' || data.$fieldMetaType === 'string' || data.$fieldMetaType === 'textarea') {
    linkable = false;
  }
  if (data.target_object === 'Opportunity' && data.target_field === 'Id') {
    linkable = false; // https://github.com/lumen-software/TruSales/issues/1313 - we won't list all the opportunities for mapping
  }

  const linkId = data.is_system_filter ? data.tag : data._id;
  let star = (<Fa name="star" className={data.is_system_filter ? 'text-warning' : 'invisible'} />);

  if (data.is_system_filter) {
    const systemTooltip = (
      <Tooltip id={`system-tooltip-${data._id}`}>System Filter</Tooltip>
    );
    star = (
      <OverlayTrigger placement="right" overlay={systemTooltip}>
        <span>{star}</span>
      </OverlayTrigger>
    );
  } else {
    star = (
      <span>{star}</span>
    );
  }

  if (linkable) {
    return (
      <Link to={`/setup/filters/${linkId}`}>{star} {value}</Link>
    );
  }
  return (
    <span>{star} {value}</span>
  );
}
LabelCellRenderer.propTypes = {
  value: PropTypes.string.isRequired, 
  data: PropTypes.shape({
    _id: PropTypes.string,
    tag: PropTypes.string,
    type: PropTypes.string,
    is_system_filter: PropTypes.bool,
  }).isRequired,
};

function TargetFieldCellRenderer(props) {
  const { value, data } = props;
  const targetObject = data.target_object;
  const targetField = value;
  const isTruSalesField = data.$is_trusales_filter;
  let display = targetField;
  if (targetObject === 'User' && indexOf(['_LicenseDate'], targetField) >= 0) {
    display = 'Tenure';
  } else if (isTruSalesField) {
    display = targetField.replace('_', '');
  }
  const truSaleTooltip = isTruSalesField ? (
    <Tooltip id={`system-tooltip-${data._id}`}>TruSale Internal Field</Tooltip>
  ) : null;

  return (
    <span>
      <code>{display}</code>
      {isTruSalesField &&
        <span>
          <OverlayTrigger placement="right" overlay={truSaleTooltip}>
            <span>&nbsp;<Fa name="info-circle" className="text-active" /></span>
          </OverlayTrigger>
        </span>
      }
    </span>
  );
}
TargetFieldCellRenderer.propTypes = {
  value: PropTypes.string.isRequired,
  data: PropTypes.shape({
    _id: PropTypes.string,
    $is_trusales_filter: PropTypes.bool,
  }).isRequired,
};


function SearchPathCellRenderer(props) {
  const { value } = props;
  if (!value) {
    return null;
  }

  const total = value.length;
  
  const labels = reduce(value, (items, v, idx) => {
    items.push((
      <code key={`${v}|${idx}`}>{v}</code>
    ));
    if (idx < total - 1) {
      items.push((
        <Fa key={`${v}|${idx}|comma`} className="text-muted" name="chevron-right" />
      ));
    }
    return items;
  }, []);

  return labels;
}
SearchPathCellRenderer.propTypes = {
  value: PropTypes.string.isRequired,
  data: PropTypes.shape({
    _id: PropTypes.string,
    search_paths: PropTypes.arrayOf(PropTypes.string),
  }).isRequired,
};



function ActionCellRenderer(props) {
  return (
    <div style={{ marginTop: -2 }}>
      <FilterButton bsSize="xs" filter={props.data} onUpdate={props.onUpdate}>
        <Fa name="edit" /> Edit
      </FilterButton>
    </div>
  );
}
ActionCellRenderer.propTypes = {
  data: PropTypes.shape({}).isRequired,
  onUpdate: PropTypes.func,
};

class FilterIndexPage extends Component {
  constructor(args) {
    super(args);
    this.state = {
      navBarHeight: 0,
    };
    this.handleSearch = this.handleSearch.bind(this);
    this.onResizeNavBar = this.onResizeNavBar.bind(this);
    this.onGridReady = this.onGridReady.bind(this);
    this.handleUpdate = this.handleUpdate.bind(this);
  }

  onGridReady(params) {
    this.gridApi = params.api;
    this.gridColumnApi = params.columnApi;
  }

  onResizeNavBar(width, navBarHeight) {
    this.setState({ navBarHeight });
  }

  handleSearch(query) {
    if (this.gridApi) {
      this.gridApi.setQuickFilter(query);
    }
  }

  handleUpdate(updatedFilter) {
    if (this.gridApi) {
      const node = this.gridApi.getRowNode(updatedFilter._id);
      if (node) {
        this.gridApi.refreshCells({ rowNodes: [node], force: true });
        this.gridApi.flashCells({ rowNodes: [node] });
      }
    }
  }

  renderTable() {
    const { navBarHeight } = this.state;
    const { filters, viewableDimensions, loading } = this.props;

    if (filters.length === 0) {
      return (
        <div className="alert alert-danger">
          No results found.
        </div>
      );
    }
    const viewableHeight = viewableDimensions.height - navBarHeight;

    
    const columnDefs = [
      {
        headerName: 'Filter Name',
        field: 'label',
        suppressMenu: true,
        width: 580,
        cellRenderer: LabelCellRenderer,
        suppressMovable: true,
      },
      {
        headerName: 'Target field',
        field: 'search_paths',
        suppressMenu: true,
        width: 350,
        suppressMovable: true,
        cellRenderer: SearchPathCellRenderer,
      },
      /*
      {
        headerName: 'Target Object',
        field: 'target_object',
        suppressMenu: true,
        width: 220,
        cellRenderer: 'targetObjectCellRenderer',
        suppressMovable: true,
      },
      {
        headerName: 'Target Field',
        field: 'target_field',
        suppressMenu: true,
        width: 210,
        suppressMovable: true,
        cellRenderer: 'targetFieldCellRenderer',
      },
      */
      {
        headerName: 'Action',
        field: '_id',
        suppressMenu: true,
        width: 100,
        suppressMovable: true,
        cellRenderer: ActionCellRenderer,
        pinned: 'right',
        cellRendererParams: {
          onUpdate: this.handleUpdate
        },
      },
    ];

    return (
      <div>
        {filters.length > 0 && <nav className="navbar navbar-default navbar-controls">
          <form className="navbar-form navbar-left">
            <SearchField collapsible={false} onSearch={this.handleSearch} />
          </form>
          {!loading && 
            <div className="navbar-form navbar-right">
              <FilterButton filter={{}}>
                <Fa name="plus" /> Add New Filter
              </FilterButton>
              &nbsp;&nbsp;
            </div>
          }
          <ReactResizeDetector handleHeight onResize={this.onResizeNavBar} />
        </nav>}

        <div className="ag-theme-alpine" style={{ height: viewableHeight, width: '100%' }}>
          <AgGridReact
            reactNext
            components={{
              agColumnHeader: CustomHeader,
            }}
            enableColResize
            enableSorting
            animateRows
            columnDefs={columnDefs}
            getRowNodeId={data => data._id}
            onGridReady={this.onGridReady}
            rowData={filters}
          />
        </div>
      </div>
    );
  }

  render() {
    const {
      loading, loaded, hasErrors, errors,
    } = this.props;
    return (
      <div>
        <Helmet title="Manage Filters" />
        <div className="row">
          <div className="col-xs-6">
            <h2 className="page-sub-header">All Filters</h2>
          </div>
        </div>
        <p />
        {loading && <ProgressBar />}
        {hasErrors && <div className="alert alert-danger">{errors.message}</div>}
        {loaded && this.renderTable()}
      </div>
    );
  }
}


// Retrieve data from store as props
function mapStateToProps(state) {
  return {
    filters: getFilters(state),
    loading: getLoading(state),
    loaded: getLoaded(state),
    hasErrors: getHasErrors(state),
    errors: getErrors(state),
    viewableDimensions: getViewableDimensions(state),
  };
}

FilterIndexPage.propTypes = {
  filters: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  loading: PropTypes.bool.isRequired,
  loaded: PropTypes.bool.isRequired,
  hasErrors: PropTypes.bool.isRequired,
  errors: PropTypes.shape({}),
  viewableDimensions: PropTypes.shape({
    width: PropTypes.number,
    height: PropTypes.number,
  }).isRequired,
};
FilterIndexPage.defaultProps = {
  errors: null,
};

FilterIndexPage.contextTypes = {
  router: PropTypes.object,
};

export default connect(mapStateToProps)(FilterIndexPage);
